import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getAuth } from '@/api/ApiCalls';
import useFundContext from '../Fund/useFundContext';
import useAuthContext from '../Auth/useAuthContext';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import useFundIsraeliIdChangeRequests from '@/hooks/useFundIsraeliIdChangeRequests';

interface BadgeCounts {
  [key: string]: number;
}

const SidebarBadgeContext = createContext<BadgeCounts>({});

export function useSidebarBadges() {
  return useContext(SidebarBadgeContext);
}

interface Props {
  children: ReactNode;
}

export default function SidebarBadgeProvider({ children }: Props) {
  const { currentFund } = useFundContext();
  const fundId = currentFund?.id;
  const { currentUser } = useAuthContext();
  const isInquiriesOn = useInquiriesFeature();
  const headers = { Authorization: `Bearer ${currentUser?.token}` };

  const { data: userUnread } = useQuery({
    queryKey: [`funds/${fundId}/inquiries/unread-count`],
    queryFn: ({ signal }) => getAuth<{ count: number }>(`funds/${fundId}/inquiries/unread-count`, headers, undefined, signal),
    /*
     * The unread dot is the only badge here, and it belongs to inquiries - so with the feature
     * off this stops asking rather than polling every 30 seconds for a count that can never be
     * shown.
     */
    enabled: isInquiriesOn && !!fundId && !!currentUser?.token,
    staleTime: 0,
    refetchInterval: 30000,
  });

  const { openCount: openIdChangeRequests } = useFundIsraeliIdChangeRequests();

  const badges = useMemo<BadgeCounts>(() => ({
    userInquiries: userUnread?.count || 0,
    israeliIdChangeRequests: openIdChangeRequests,
  }), [userUnread?.count, openIdChangeRequests]);

  return (
    <SidebarBadgeContext.Provider value={badges}>
      {children}
    </SidebarBadgeContext.Provider>
  );
}
